/**
 * SidebarSnoozePopover (iter200)
 *
 * Popover that opens when the user clicks the numeric badge count next
 * to the Sentiment Alerts sidebar icon. Lists the newest 5 unread alerts
 * with a three-button snooze menu per row (1h / 24h / Until Monday).
 *
 * UX contract:
 *   - Clicking the Sentiment Alerts ICON navigates to /dashboard/alerts
 *     (parent component owns the button).
 *   - Clicking the BADGE NUMBER opens this popover; icon click still
 *     navigates. Parent MUST call e.stopPropagation on the badge.
 *
 * Snoozing an alert removes it from `/api/sentiment-alerts/unread-count`
 * server-side, so the badge counter refreshes on the next tick. To
 * avoid a stale-count flicker we optimistically decrement locally.
 */
import React, { useEffect, useState } from 'react';
import { Clock, Loader2, Moon, MoonStar } from 'lucide-react';
import { toast } from 'sonner';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const SNOOZE_OPTIONS = [
  { key: '1h', label: '1 hour', shortLabel: '1h' },
  { key: '24h', label: '24 hours', shortLabel: '24h' },
  { key: 'until_monday', label: 'Until Monday', shortLabel: 'Mon' },
];

const formatRelative = (iso) => {
  try {
    const d = new Date(iso);
    const diffSec = (Date.now() - d.getTime()) / 1000;
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  } catch {
    return '';
  }
};

const SidebarSnoozePopover = ({ children, onSnoozed }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [busyAlertId, setBusyAlertId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/sentiment-alerts', { params: { limit: 5 } });
        if (!cancelled) {
          const list = (r.data?.alerts || []).filter((a) => !a.is_read);
          setAlerts(list);
        }
      } catch {
        if (!cancelled) setAlerts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleSnooze = async (alertId, duration) => {
    setBusyAlertId(alertId);
    try {
      await api.post(`/sentiment-alerts/${alertId}/snooze`, { duration });
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      toast.success(
        duration === 'until_monday'
          ? 'Snoozed until Monday'
          : `Snoozed for ${duration}`,
      );
      onSnoozed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to snooze alert');
    } finally {
      setBusyAlertId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        className="w-80 p-0"
        data-testid="sidebar-snooze-popover"
      >
        <div className="px-3 py-2 border-b border-[#1F2A4D] flex items-center gap-2">
          <MoonStar className="h-3.5 w-3.5 text-rose-400" />
          <span className="text-xs font-medium text-slate-100">
            Snooze unread alerts
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-6" data-testid="sidebar-snooze-loading">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-slate-400" data-testid="sidebar-snooze-empty">
            No unread alerts right now.
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto" data-testid="sidebar-snooze-list">
            {alerts.map((a) => (
              <li
                key={a.id}
                className="px-3 py-2 border-b border-[#1F2A4D] last:border-b-0"
                data-testid={`sidebar-snooze-item-${a.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <p className="text-xs text-slate-100 leading-snug line-clamp-2">
                    {a.title || a.watch_name || 'Sentiment alert'}
                  </p>
                  <span className="text-[10px] text-slate-500 shrink-0 mt-0.5 inline-flex items-center gap-0.5">
                    <Clock className="h-2.5 w-2.5" />
                    {formatRelative(a.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {SNOOZE_OPTIONS.map((opt) => (
                    <Button
                      key={opt.key}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-[10px] h-6 px-2 text-slate-300 hover:bg-[#131C3A] hover:text-slate-100 border border-[#1F2A4D]"
                      disabled={busyAlertId === a.id}
                      onClick={() => handleSnooze(a.id, opt.key)}
                      data-testid={`sidebar-snooze-btn-${a.id}-${opt.key}`}
                    >
                      {busyAlertId === a.id
                        ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        : <Moon className="h-2.5 w-2.5 mr-0.5" />}
                      {opt.shortLabel}
                    </Button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default SidebarSnoozePopover;
